import React, { useCallback } from "react";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";
import {
  Issue,
  IssueAttributes,
  IssueFieldId,
  IssueStatus,
} from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import type { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { getWatcherProp } from "@adsk/acc-issues-core/state/redux/ducks/watchers";
import {
  EditableCellRendererProps,
  IssueTableMeta,
  InlineFieldTableCell,
  isFieldEditable,
  useIssueFieldInlineChange,
  useIsFieldRequired,
} from "./inlineEditableCellShared";
import {
  InlineEditDropdownPresentationProvider,
  InlineEditTextPresentationProvider,
} from "../../../../utils/components/IssueFieldPresentationContext";
import type {
  IssueChangeFn,
  IssueChange,
  DefaultEditFieldInterface,
} from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/typesForFields";
import { sanitizeIssueAttributeMap } from "../../../../utils/data/issueFieldSanitization";
import { getAreAllRequiredFieldsFilled } from "../../../../utils/data/issueRequiredFields";
import { StatusField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/StatusField";
import { RootCauseField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/RootCauseField";
import { AssignedToField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/AssignedToField";
import { DetailPanelTypeField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/DetailPanelTypeField";
import { TitleField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/TitleField";
import { DescriptionField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/DescriptionField";
import { LocationDetailsField } from "../../../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/LocationDetailsField";
import { FloatingPanel } from "../issuesTableStyles";

type InlineFieldType = "text" | "dropdown";

const PRESENTATION_PROVIDERS: Record<
  InlineFieldType,
  React.ComponentType<{ children: React.ReactNode }>
> = {
  dropdown: InlineEditDropdownPresentationProvider,
  text: InlineEditTextPresentationProvider,
};

const InlineEditCellWrapper = ({
  fieldType,
  children,
}: {
  fieldType: InlineFieldType;
  children: React.ReactNode;
}) => {
  const PresentationProvider = PRESENTATION_PROVIDERS[fieldType];

  return (
    <InlineFieldTableCell testId={`editable-${fieldType}-cell`}>
      <PresentationProvider>{children}</PresentationProvider>
    </InlineFieldTableCell>
  );
};

interface TextFieldComponentProps {
  T: ReturnType<typeof useTranslations>["T"];
  handleChange: IssueChangeFn;
  issue: Issue;
  required?: boolean;
}

const InlineEditTextCell = React.memo(
  ({
    issue,
    fieldKey,
    fieldComponent: FieldComponent,
  }: {
    issue: Issue;
    fieldKey: IssueFieldId;
    fieldComponent: React.ComponentType<TextFieldComponentProps>;
  }) => {
    const { T } = useTranslations();
    const isEditable = isFieldEditable(issue, fieldKey);
    const inlineChange = useIssueFieldInlineChange();
    const required = useIsFieldRequired(issue, fieldKey);

    const handleChange: IssueChangeFn = useCallback(
      (change: IssueChange) => {
        if (!(fieldKey in change)) return;
        const value = (change as Partial<Issue>)[fieldKey as keyof Issue];
        inlineChange(issue, { [fieldKey]: value } as Partial<IssueAttributes>);
      },
      [inlineChange, issue, fieldKey]
    );

    if (!isEditable) {
      return (
        <OverflowTooltip>
          {issue[fieldKey as keyof Issue] ?? ""}
        </OverflowTooltip>
      );
    }

    return (
      <InlineEditCellWrapper fieldType="text">
        <FieldComponent
          T={T}
          issue={issue}
          handleChange={handleChange}
          required={required}
        />
      </InlineEditCellWrapper>
    );
  }
);

InlineEditTextCell.displayName = "InlineEditTextCell";

type DropdownFieldComponentProps = DefaultEditFieldInterface;

const InlineEditDropdownCell = React.memo(
  ({
    issue,
    fieldKey,
    fieldComponent: FieldComponent,
    containerRef,
  }: {
    issue: Issue;
    fieldKey: IssueFieldId;
    fieldComponent: React.ComponentType<DropdownFieldComponentProps>;
    containerRef?: React.RefObject<HTMLDivElement | null>;
  }) => {
    const inlineChange = useIssueFieldInlineChange();
    const required = useIsFieldRequired(issue, fieldKey);

    const onChange: IssueChangeFn = useCallback(
      (change: IssueChange, handlers?) => {
        const sanitizedChanges = sanitizeIssueAttributeMap(
          change as Partial<IssueFormAttributes>
        );
        inlineChange(issue, sanitizedChanges, handlers);
      },
      [inlineChange, issue]
    );

    return (
      <InlineEditCellWrapper fieldType="dropdown">
        <FieldComponent
          issue={issue}
          onChange={onChange}
          containerRef={containerRef}
          required={required}
        />
      </InlineEditCellWrapper>
    );
  }
);

InlineEditDropdownCell.displayName = "InlineEditDropdownCell";

type TextFieldConfig = {
  fieldType: "text";
  fieldKey: IssueFieldId;
  fieldComponent: React.ComponentType<TextFieldComponentProps>;
};

type DropdownFieldConfig = {
  fieldType: "dropdown";
  fieldKey: IssueFieldId;
  fieldComponent: React.ComponentType<DropdownFieldComponentProps>;
};

type InlineEditFieldConfig = TextFieldConfig | DropdownFieldConfig;

function createTextCellRenderer(config: TextFieldConfig) {
  const CellRenderer = ({ row }: EditableCellRendererProps) => (
    <InlineEditTextCell
      issue={row.original}
      fieldKey={config.fieldKey}
      fieldComponent={config.fieldComponent}
    />
  );
  CellRenderer.displayName = `InlineEditCellRenderer(${config.fieldKey})`;
  return CellRenderer;
}

function createDropdownCellRenderer(config: DropdownFieldConfig) {
  const CellRenderer = ({ row, table }: EditableCellRendererProps) => {
    const meta = table.options.meta as IssueTableMeta | undefined;
    return (
      <InlineEditDropdownCell
        issue={row.original}
        fieldKey={config.fieldKey}
        fieldComponent={config.fieldComponent}
        containerRef={meta?.container}
      />
    );
  };
  CellRenderer.displayName = `InlineEditCellRenderer(${config.fieldKey})`;
  return CellRenderer;
}

function createInlineEditCellRenderer(config: InlineEditFieldConfig) {
  switch (config.fieldType) {
    case "text":
      return createTextCellRenderer(config);
    case "dropdown":
      return createDropdownCellRenderer(config);
  }
}

const InlineDescriptionField = (props: TextFieldComponentProps) => (
  <FloatingPanel>
    <DescriptionField {...props} />
  </FloatingPanel>
);

InlineDescriptionField.displayName = "InlineDescriptionField";

export const editableTitleCellRenderer = createInlineEditCellRenderer({
  fieldType: "text",
  fieldKey: "title",
  fieldComponent: TitleField,
});

export const editableDescriptionCellRenderer = createInlineEditCellRenderer({
  fieldType: "text",
  fieldKey: "description",
  fieldComponent: InlineDescriptionField,
});

export const editableLocationDetailsCellRenderer = createInlineEditCellRenderer(
  {
    fieldType: "text",
    fieldKey: "locationDetails",
    fieldComponent: LocationDetailsField,
  }
);

const InlineStatusField = React.memo((props: DropdownFieldComponentProps) => {
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const watcherProp = useSelector(getWatcherProp);

  const issueType = props.issue.issueTypeId
    ? issueTypeEntitiesMap[props.issue.issueTypeId]
    : undefined;
  const fieldsMetadata = issueType?.fieldsMetadata ?? [];

  const isDisabledDueToRequiredFields =
    props.issue.status === IssueStatus.Draft &&
    !getAreAllRequiredFieldsFilled({
      watcherProp,
      fieldsMetadata,
      issue: props.issue,
    });

  return (
    <StatusField
      {...props}
      isDisabledDueToRequiredFields={isDisabledDueToRequiredFields}
    />
  );
});

export const editableStatusCellRenderer = createInlineEditCellRenderer({
  fieldType: "dropdown",
  fieldKey: "status",
  fieldComponent: InlineStatusField,
});

export const editableRootCauseCellRenderer = createInlineEditCellRenderer({
  fieldType: "dropdown",
  fieldKey: "rootCauseId",
  fieldComponent: RootCauseField,
});

export const editableAssignedToCellRenderer = createInlineEditCellRenderer({
  fieldType: "dropdown",
  fieldKey: "assignedTo",
  fieldComponent: AssignedToField,
});

export const editableTypeCellRenderer = createInlineEditCellRenderer({
  fieldType: "dropdown",
  fieldKey: "issueTypeId",
  fieldComponent: DetailPanelTypeField,
});
